import React, {createContext, useContext, useRef} from 'react';
import { FormSchemaType } from "../schemas/formSchema";
import { FieldErrors } from "react-hook-form";
import {fireAlert} from "../services/alert";

interface InputFieldsProps {
    formData: Partial<FormSchemaType>;
    onSubmit: <T extends Partial<FormSchemaType>>(data: T) => void;
    onError: <T extends Partial<FormSchemaType>>(errors: FieldErrors<T>) => void;
}

const InputFieldsContext = createContext<InputFieldsProps | undefined>(undefined);

export const InputFieldsProvider: React.FC<React.PropsWithChildren<{}>> = ({children}) => {
    //get form data from local storage in first render
    const localStorageData = localStorage.getItem('formData');
    const initialFormData = localStorageData ? JSON.parse(localStorageData) : {};

    const formDataRef = useRef<Partial<FormSchemaType>>(initialFormData);

    const onSubmit = <T extends Partial<FormSchemaType>>(data: T) => {
        Object.assign(formDataRef.current, data);
    };

    const onError = <T extends Partial<FormSchemaType>>(errors: FieldErrors<T>) => {
        if(errors.captchaStepOne) {
            fireAlert({
                icon: 'error',
                title: 'Erro de captcha!',
                text: 'Aguarde um momente e tente novamente!',
                confirmButtonText: 'OK'
            })
            return;
        }
        const firstError = Object.values(errors)[0];

        // Clear any existing error popovers first
        const existingPopovers = document.querySelectorAll('.error-popover');
        existingPopovers.forEach(popover => popover.remove());

        // Attach a popover error component after the first input
        const input = firstError!.ref as HTMLInputElement;

        if (input) {
            const errorElement = document.createElement('div');
            errorElement.className = `
                error-popover
                top-full
                absolute
                left-1/2
                transform
                -translate-x-1/2
                mt-1
                z-10
                opacity-0
                scale-95
                transition-all
                duration-300
                ease-in-out
            `;
            errorElement.innerHTML = `
                <div
                    class="
                        relative
                        flex
                        justify-center
                        bg-background-white
                        text-tertiary
                        text-sm
                        rounded-full
                        px-6
                        py-2
                        border-b-2
                        border-primary
    
                        before:absolute
                        before:content-['']
                        before:w-3
                        before:h-3
                        before:bg-background-white
                        before:transform
                        before:rotate-45
                        before:translate-y-1/2
                        before:bottom-full
                    ">
                    ${firstError!.message}
                </div>
            `;
            input.parentNode!.insertBefore(errorElement, input.nextSibling);

            // Trigger animation after insertion with shorter delay
            setTimeout(() => {
                errorElement.style.opacity = '1';
                errorElement.style.transform = 'translateX(-50%) scale(1)';
            }, 10);

            // Add event listener to remove popover on any user interaction
            const removePopover = () => {
                errorElement.style.opacity = '0';
                errorElement.style.transform = 'translateX(-50%) scale(0.95)';

                // Remove after animation completes
                setTimeout(() => {
                    if (errorElement.parentNode) {
                        errorElement.remove();
                    }
                }, 300);

                // Remove the event listeners to avoid memory leaks
                document.removeEventListener('click', removePopover);
                document.removeEventListener('keydown', removePopover);
            };

            // Add event listeners for different types of interactions
            document.addEventListener('click', removePopover);
            document.addEventListener('keydown', removePopover);
        }
    };

    return (
        <InputFieldsContext.Provider value={{formData: formDataRef.current, onError, onSubmit}}>
            {children}
        </InputFieldsContext.Provider>
    );
};

export const useInputFields = () => {
    const ctx = useContext(InputFieldsContext);
    if (!ctx) {
        throw new Error('useInputFields must be used inside a <InputFieldsProvider>');
    }
    return ctx;
};

